import {
  listAdminWorkspaces,
  updateAdminWorkspace,
  type CoworkAdminWorkspace,
  type CoworkWorkspacePlan,
} from "@/cowork/api"
import { SettingsListV2 } from "@/components/settings-v2/parts/list"
import { SettingsRowV2 } from "@/components/settings-v2/parts/row"
import { useLanguage } from "@/context/language"
import { createMemo, createResource, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

const PLANS: CoworkWorkspacePlan[] = ["internal", "early_access", "trial", "platform", "enterprise"]

type Draft = { plan: CoworkWorkspacePlan; seat_limit: string }

export function CoworkAdminWorkspacesPage() {
  const language = useLanguage()
  const [state, setState] = createStore({
    month: new Date().toISOString().slice(0, 7),
    drafts: {} as Record<string, Draft | undefined>,
    busy: "",
    error: "",
    notice: "",
  })

  const [workspaces, actions] = createResource(() => state.month, listAdminWorkspaces)
  const total = createMemo(() =>
    workspaces.error
      ? 0
      : (workspaces.latest ?? []).reduce((sum, workspace) => sum + workspace.estimated_invoice_eur, 0),
  )

  const draft = (workspace: CoworkAdminWorkspace): Draft =>
    state.drafts[workspace.id] ?? { plan: workspace.plan, seat_limit: String(workspace.seat_limit) }

  const changed = (workspace: CoworkAdminWorkspace) => {
    const value = draft(workspace)
    return value.plan !== workspace.plan || value.seat_limit !== String(workspace.seat_limit)
  }

  const save = async (workspace: CoworkAdminWorkspace) => {
    const value = draft(workspace)
    const seatLimit = Number(value.seat_limit)
    if (state.busy || !Number.isInteger(seatLimit) || seatLimit < 1) return
    setState({ busy: workspace.id, error: "", notice: "" })
    try {
      await updateAdminWorkspace(workspace.id, { plan: value.plan, seat_limit: seatLimit })
      setState("drafts", workspace.id, undefined)
      setState("notice", language.t("cowork.adminWorkspaces.saved", { name: workspace.name }))
      await actions.refetch()
    } catch (error) {
      setState("error", error instanceof Error ? error.message : String(error))
    } finally {
      setState("busy", "")
    }
  }

  return (
    <div class="min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10">
      <div class="mx-auto max-w-[1100px]">
        <header class="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.adminWorkspaces.title")}</h1>
            <p class="mt-1 text-[13px] text-v2-text-text-muted">{language.t("cowork.adminWorkspaces.description")}</p>
          </div>
          <input
            class="h-9 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-13-regular text-v2-text-text-base outline-none"
            type="month"
            value={state.month}
            aria-label={language.t("cowork.adminWorkspaces.month")}
            onChange={(event) => event.currentTarget.value && setState("month", event.currentTarget.value)}
          />
        </header>

        <div class="mt-7 space-y-7">
          <Show when={state.notice || state.error}>
            <SettingsListV2>
              <SettingsRowV2
                title={
                  state.error ? language.t("cowork.adminWorkspaces.error") : language.t("cowork.adminWorkspaces.done")
                }
                description={state.error || state.notice}
              >
                <span />
              </SettingsRowV2>
            </SettingsListV2>
          </Show>

          <Show when={!workspaces.error}>
            <SettingsListV2>
              <SettingsRowV2
                title={language.t("cowork.adminWorkspaces.total", { month: state.month, total: String(total()) })}
                description={language.t("cowork.adminWorkspaces.totalDescription")}
              >
                <span />
              </SettingsRowV2>
            </SettingsListV2>
          </Show>

          <SettingsListV2>
            <Show
              when={!workspaces.error}
              fallback={
                <SettingsRowV2
                  title={language.t("cowork.adminWorkspaces.error")}
                  description={workspaces.error instanceof Error ? workspaces.error.message : String(workspaces.error)}
                >
                  <span />
                </SettingsRowV2>
              }
            >
              <Show
                when={!workspaces.loading && (workspaces()?.length ?? 0) > 0}
                fallback={
                  <SettingsRowV2
                    title={
                      workspaces.loading
                        ? language.t("cowork.adminWorkspaces.loading")
                        : language.t("cowork.adminWorkspaces.empty")
                    }
                    description=""
                  >
                    <span />
                  </SettingsRowV2>
                }
              >
                <For each={workspaces() ?? []}>
                  {(workspace) => (
                    <SettingsRowV2
                      title={workspace.name}
                      description={
                        <>
                          <span class="block">
                            {language.t("cowork.adminWorkspaces.summary", {
                              slug: workspace.slug,
                              owner: workspace.owner_email || "-",
                              companies: String(workspace.companies),
                              used: String(workspace.seats_used),
                              limit: String(workspace.seat_limit),
                            })}
                          </span>
                          <span class="block">
                            {language.t("cowork.adminWorkspaces.usage", {
                              active: String(workspace.active_companies),
                              companies: String(workspace.companies),
                              runs: String(workspace.report_runs),
                              invoice: String(workspace.estimated_invoice_eur),
                            })}
                          </span>
                          <Show when={workspace.fair_use_companies.length}>
                            <span class="block text-v2-state-fg-danger">
                              {language.t("cowork.adminWorkspaces.fairUse", {
                                companies: workspace.fair_use_companies.join(", "),
                              })}
                            </span>
                          </Show>
                        </>
                      }
                    >
                      <div class="flex flex-wrap items-center justify-end gap-2">
                        <select
                          class="h-8 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-[12px] text-v2-text-text-base outline-none"
                          value={draft(workspace).plan}
                          aria-label={language.t("cowork.adminWorkspaces.plan")}
                          disabled={!!state.busy}
                          onChange={(event) =>
                            setState("drafts", workspace.id, {
                              ...draft(workspace),
                              plan: event.currentTarget.value as CoworkWorkspacePlan,
                            })
                          }
                        >
                          <For each={PLANS}>
                            {(plan) => (
                              <option value={plan}>{language.t(`cowork.adminWorkspaces.plan.${plan}`)}</option>
                            )}
                          </For>
                        </select>
                        <input
                          class="h-8 w-20 rounded-md border border-v2-border-border-base bg-v2-background-bg-layer-01 px-2 text-[12px] text-v2-text-text-base outline-none"
                          type="number"
                          min="1"
                          value={draft(workspace).seat_limit}
                          aria-label={language.t("cowork.adminWorkspaces.seatLimit")}
                          disabled={!!state.busy}
                          onInput={(event) =>
                            setState("drafts", workspace.id, {
                              ...draft(workspace),
                              seat_limit: event.currentTarget.value,
                            })
                          }
                        />
                        <button
                          type="button"
                          class="h-8 rounded-md bg-v2-background-bg-contrast px-3 text-[11px] font-semibold text-v2-text-text-contrast disabled:opacity-50"
                          disabled={!!state.busy || !changed(workspace)}
                          onClick={() => void save(workspace)}
                        >
                          {state.busy === workspace.id
                            ? language.t("cowork.adminWorkspaces.saving")
                            : language.t("cowork.adminWorkspaces.save")}
                        </button>
                      </div>
                    </SettingsRowV2>
                  )}
                </For>
              </Show>
            </Show>
          </SettingsListV2>
        </div>
      </div>
    </div>
  )
}
